import { productNames, type FamilyProductId } from '@beyond-js/ui/react';
import type { Descriptor } from '@snapshots/domain';

/** The way back to another product: its display name and the exact place to reopen */
export interface Return {
	product: FamilyProductId | 'packages';
	name: string;
	href: string;
}

interface Known {
	/** The family bar's descriptor for the place in view, as the shell holds it */
	descriptor: Descriptor | { unavailable: true } | null;
	/** The project in view, which the way back reopens in the other product */
	project?: string;
	/** The organization of the project in view, which Packages is opened in with the project */
	organization?: string;
	/** This application's configured Packages interface, for a capture opened from Packages */
	packages?: string;
	/** This application's configured Projects interface, for a person sent here by Projects */
	projects?: string;
}

/**
 * The way back for a page opened from another product (`?from=<product>`, decision D54). The
 * identifier is only a name: the address is never taken from the link that arrived, but from what this
 * application knows itself, the project's entry in that product as Beyond Projects describes it (the
 * family bar's descriptor) or, for Projects, its configured interface with the project in view. An
 * identifier this application cannot place, or names itself, draws no line.
 */
export class Way {
	static PARAMETER = 'from';
	/** Packages is a family product the shared names do not list yet; its name is never translated */
	static PACKAGES = 'Packages';

	static find(from: string | null | undefined, { descriptor, project, projects, organization, packages }: Known): Return | undefined {
		if (from === 'packages') return Way.#packages(descriptor, { project, organization, packages });
		if (!from || from === 'snapshots' || !Object.hasOwn(productNames, from)) return undefined;
		const product = from as FamilyProductId;
		const described = descriptor && !('unavailable' in descriptor) ? descriptor : undefined;
		const entry = described?.products?.find(item => item.product === product && item.url);
		const href = entry?.url ?? (product === 'projects' ? Way.#projects(described?.links?.home || projects, project) : undefined);
		return href ? { product, name: productNames[product], href } : undefined;
	}

	/**
	 * The name of the product a page was opened from, when this application can place a way back to it once it
	 * knows the place in view: Packages or a family product, never this one
	 */
	static named(from: string | null | undefined): string | undefined {
		if (from === 'packages') return Way.PACKAGES;
		if (!from || from === 'snapshots' || !Object.hasOwn(productNames, from)) return undefined;
		return productNames[from as FamilyProductId];
	}

	/**
	 * Packages at the same context: its entry for the project in the descriptor, else its configured
	 * interface with the organization and project (as Projects enters Packages), else nothing
	 */
	static #packages(descriptor: Known['descriptor'], { project, organization, packages }: Pick<Known, 'project' | 'organization' | 'packages'>): Return | undefined {
		const described = descriptor && !('unavailable' in descriptor) ? descriptor : undefined;
		const entry = described?.products?.find(item => item.product === 'packages' && item.url)?.url;
		let href = entry ?? undefined;
		if (!href && packages && project && organization) {
			try {
				const url = new URL(packages);
				url.searchParams.set('organization', organization);
				url.searchParams.set('project', project);
				href = url.href;
			} catch {
				href = undefined;
			}
		}
		return href ? { product: 'packages', name: Way.PACKAGES, href } : undefined;
	}

	/** Projects' own page for the project in view, or its home */
	static #projects(home: string | undefined, project: string | undefined): string | undefined {
		if (!home) return undefined;
		try {
			const url = new URL(home);
			if (project) url.searchParams.set('project', project);
			return url.href;
		} catch {
			return undefined;
		}
	}
}
